import { ChangeEvent, useCallback } from 'react';
import {
  ImageField,
  FormDataConsumer,
  InputHelperText,
  InputProps,
  useInput
} from 'react-admin';
import classNames from 'classnames';
import get from 'lodash/get';
import {
  Uppload,
  Local,
  Preview,
  Crop,
  Blur,
  Brightness,
  Camera,
  Contrast,
  Grayscale,
  HueRotate,
  Invert,
  Saturate,
  Sepia,
  Unsplash,
  IUpploadSettings
} from 'uppload';
import {
  Fab,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Tooltip,
  makeStyles
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

import tusUploader from '../tusUploader';

import './style.css';
import ptBR from './ptBR';

type UploadImageInputProps = InputProps & {
  cropOptions?: {
    aspectRatio?: number;
    aspectRatioOptions?: {
      [index: string]: number;
    };
    hideAspectRatioSettings?: boolean;
    autoCropArea?: 1 | 2 | 3 | undefined;
    viewMode?: 1 | 2 | 3 | undefined;
  };
  effects?: string[];
  mimeTypes?: string[];
  services?: string[];
  updateFieldCallback?: (event: Partial<ChangeEvent<any>>) => void;
  upploadOptions?: Partial<IUpploadSettings>;
};

interface CreateUpploadProps {
  cropOptions?: {
    aspectRatio?: number;
    aspectRatioOptions?: {
      [index: string]: number;
    };
    hideAspectRatioSettings?: boolean;
    autoCropArea?: 1 | 2 | 3 | undefined;
    viewMode?: 1 | 2 | 3 | undefined;
  };
  effects?: string[];
  mimeTypes?: string[];
  services?: string[];
  updateFieldCallback: (event: Partial<ChangeEvent<any>>) => void;
  upploadOptions?: Partial<IUpploadSettings>;
}

/*
const defaultEffects = [
  'crop',
  'camera',
  'unsplash',
  'blur',
  'brightness',
  'constrast',
  'grayscale',
  'hueRotate',
  'invert',
  'saturate',
  'sepia'
];
*/

const defaultEffects = [
  'crop',
  'blur',
  'brightness',
  'constrast',
  'grayscale',
  'hueRotate',
  'invert',
  'saturate',
  'sepia'
];

const unsplashApiKey = 'FXBjPIDCQViectqJ8qA3v57CKtpzAAO9BwRjYe_xCjs';

const useStyles = makeStyles(theme => ({
  root: { boxSizing: 'border-box', padding: '23px 12px 6px' }
}));

function createUppload({
  updateFieldCallback,
  upploadOptions = {},
  services = ['local'],
  effects = defaultEffects,
  mimeTypes = ['image/png', 'image/jpeg', 'image/gif'],
  cropOptions = {}
}: CreateUpploadProps) {
  const uppload = new Uppload({
    lang: ptBR,
    defaultService: 'local',
    uploader: tusUploader,
    ...upploadOptions
  });

  uppload.use(new Local({ mimeTypes }));
  if (effects.length === 0) {
    uppload.use(new Preview());
  }

  if (effects.includes('crop')) uppload.use(new Crop(cropOptions));
  if (services.includes('camera')) uppload.use(new Camera());
  if (services.includes('unsplash')) uppload.use(new Unsplash(unsplashApiKey));
  if (effects.includes('blur')) uppload.use(new Blur());
  if (effects.includes('brightness')) uppload.use(new Brightness());
  if (effects.includes('constrast')) uppload.use(new Contrast());
  if (effects.includes('grayscale')) uppload.use(new Grayscale());
  if (effects.includes('hueRotate')) uppload.use(new HueRotate());
  if (effects.includes('invert')) uppload.use(new Invert());
  if (effects.includes('saturate')) uppload.use(new Saturate());
  if (effects.includes('sepia')) uppload.use(new Sepia());

  uppload.on('upload', image =>
    updateFieldCallback({ target: { value: image } })
  );

  uppload.on('close', () => {
    const elements = document.getElementsByClassName('uppload-container');
    for (let i = 0; i < elements.length; i++) {
      elements.item(i)?.remove();
    }
  });

  return uppload;
}

export default function UploadImageInput(props: UploadImageInputProps) {
  const {
    services,
    effects,
    mimeTypes,
    cropOptions,
    record,
    source,
    validate,
    className,
    helperText,
    upploadOptions,
    label,
    ...rest
  } = props;
  const classes = useStyles(props);

  const {
    input: { onChange },
    meta: { error, touched },
    isRequired
  } = useInput(props);

  const openModal = useCallback(
    e => {
      e.preventDefault();
      const upploader = createUppload({
        updateFieldCallback: onChange,
        upploadOptions,
        services,
        effects,
        mimeTypes,
        cropOptions
      });
      upploader.open();
    },
    [cropOptions, onChange, effects, mimeTypes, upploadOptions, services]
  );

  return (
    <>
      <FormControl
        {...rest}
        fullWidth
        variant="filled"
        margin="dense"
        className={classNames(
          classes.root,
          className,
          'MuiFilledInput-root',
          'MuiFilledInput-underline'
        )}
      >
        {label && (
          <InputLabel
            shrink
            htmlFor={props.id}
            id={label && props.id ? `${props.id}-label` : undefined}
          >
            {label}
            {isRequired && '\u00a0*'}
          </InputLabel>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <FormDataConsumer>
              {({ formData, ...rest }) => {
                const value = get(formData, source);
                if (!value) {
                  return null;
                }

                const styles = { width: '100%', maxWidth: '350px' };
                if (value.type && value.type.match(/video\//i)) {
                  return <video src={value.url} style={styles} controls />;
                } else {
                  return <ImageField source="url" record={value} {...rest} />;
                }
              }}
            </FormDataConsumer>
          </Grid>

          <Grid item>
            <Tooltip aria-label="upload picture" title="Enviar arquivo">
              <Fab aria-label="upload picture" onClick={openModal}>
                <CloudUploadIcon />
              </Fab>
            </Tooltip>
          </Grid>
        </Grid>

        {helperText && (
          <FormHelperText error={touched && error}>
            <InputHelperText
              helperText={helperText}
              touched={!!touched}
              error={error}
            />
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
}
